import { Signal, signal } from '@preact/signals'
import { useEffect, useRef, useState } from 'preact/hooks'
import type { ComponentChildren } from 'preact'
import { FileCheck, OctagonX, X } from 'lucide-preact'

// return time at each animation frames

export const eachFrame = new Signal({ now: Date.now(), delta: 0 })
;(function rafLoop() {
  const old = eachFrame.peek()
  const now = Date.now()
  eachFrame.value = { now, delta: now - old.now }
  requestAnimationFrame(rafLoop)
})()

const statusColors = {
  success: 'success',
  failure: 'error',
} as const

const statusIcons = {
  success: FileCheck, // TODO: replace by more generic check
  failure: OctagonX,
} as const

type Notification = {
  at: number
  status: keyof typeof statusColors
  icon?: typeof X
  title?: string
  description: ComponentChildren
  dismissed: boolean
}

const TimeProgress = (
  { start, duration, onEnd, status }: {
    start: number
    duration: number
    onEnd: () => void
    status: string
  },
) => {
  const { now } = eachFrame.value
  const elapsed = now - start
  const percent = 1 - Math.max(Math.min(elapsed / duration, 1), 0)
  const notEnded = useRef(true)

  if (notEnded.current && elapsed > duration) {
    notEnded.current = false
    onEnd()
  }

  return (
    <progress
      class={`progress ${
        status === 'success' ? 'progress-success' : 'progress-error'
      } `}
      value={`${percent * 100}`}
      max='100'
    >
    </progress>
  )
}

type LifecycleHandler<T extends HTMLElement> = (
  elem: T,
) => ((elem: T | null) => void) | void

const NotificationCard = (
  { notification, dismiss }: {
    notification: Notification
    dismiss: (n: Notification) => void
  },
) => {
  const duration = 10_000

  const title = notification.title || notification.status
  const Icon = notification.icon || statusIcons[notification.status]
  const divRef = useRef<HTMLDivElement | null>(null)
  const [inactiveSince, setInactiveSince] = useState(notification.at)
  const [isClosed, setIsClosed] = useState(false)

  useEffect(() => {
    const div = divRef.current
    if (!div) return

    let elapsed = 0
    let activatedSince = 0
    const timings = { mouse: -notification.at, focus: -notification.at }

    const activate = (
      event:
        | HTMLElementEventMap['mouseenter']
        | HTMLElementEventMap['mousemove']
        | HTMLElementEventMap['focusin'],
    ) => {
      const now = Date.now()
      timings[event.type.slice(0, 5) as keyof typeof timings] = now
      inactiveSince && setInactiveSince(0)
      activatedSince || (activatedSince = now)
    }

    const desactivate = (
      event:
        | HTMLElementEventMap['mouseleave']
        | HTMLElementEventMap['focusout'],
    ) => {
      const now = Date.now()
      timings[event.type.slice(0, 5) as keyof typeof timings] = -now
      if (timings.focus > 0 || timings.mouse > 0) return
      elapsed += now - activatedSince
      activatedSince = 0
      setInactiveSince(now - elapsed)
    }

    div.addEventListener('mouseenter', activate, { passive: true })
    div.addEventListener('mousemove', activate, { passive: true })
    div.addEventListener('mouseleave', desactivate, { passive: true })
    div.addEventListener('focusin', activate, { passive: true })
    div.addEventListener('focusout', desactivate, { passive: true })

    return () => {
      div.removeEventListener('mouseenter', activate)
      div.removeEventListener('mousemove', activate)
      div.removeEventListener('mouseleave', desactivate)
      div.removeEventListener('focusin', activate)
      div.removeEventListener('focusout', desactivate)
    }
  }, [notification.at, inactiveSince])

  const close = () => {
    if (isClosed) return
    setIsClosed(true)
  }

  const handleTransitionEnd = (e: TransitionEvent) => {
    if (e.propertyName === 'opacity' && e.target === divRef.current) {
      dismiss(notification)
    }
  }

  return (
    <div
      ref={divRef}
      class='
      transition-all duration-300 ease-in-out
      data-[closed]:opacity-0
      data-[closed]:translate-y-2
      data-[closed]:sm:translate-x-2
      data-[closed]:sm:translate-y-0'
      data-closed={isClosed ? true : undefined}
      onTransitionEnd={handleTransitionEnd}
    >
      {!inactiveSince ? null : (
        <TimeProgress
          start={inactiveSince}
          duration={duration}
          onEnd={close}
          status={notification.status}
        />
      )}
      <div
        role='alert'
        class={`alert ${
          notification.status === 'success' ? 'alert-success' : 'alert-error'
        }`}
      >
        <Icon class='h-8 w-8' />
        <div>
          <h3 class='font-bold'>{title}</h3>
          <div class='text-sm'>{notification.description}</div>
        </div>
        <div>
          <button
            type='button'
            onClick={close}
            class='cursor-pointer'
          >
            <span class='sr-only'>Close</span>
            <X class='h-5 w-5' />
          </button>
        </div>
      </div>
    </div>
  )
}

const currentNotifications = signal<Notification[]>([])

function dismissNotification(notification: Notification) {
  if (notification.dismissed) return
  notification.dismissed = true
  currentNotifications.value = currentNotifications.peek()
    .filter((n) => !n.dismissed)
}

export const notify = (n: Omit<Notification, 'at' | 'dismissed'>) => {
  currentNotifications.value = [...currentNotifications.peek(), {
    ...n,
    at: performance.timeOrigin + performance.now(),
    dismissed: false,
  }]
}

export const Notifications = () => (
  <div
    aria-live='assertive'
    class='toast toast-end z-50 sm:toast-top p-4'
  >
    {currentNotifications.value.map((notification) => (
      <NotificationCard
        notification={notification}
        key={notification.at}
        dismiss={dismissNotification}
      />
    ))}
  </div>
)
